import { useFpCkoRedirectApmExecution } from '@cart-services/hooks/use-fp-cko-redirect-apm-execution'
import { useFpCampaignCartService } from '@cart-services/hooks/useFpCampaignCartService'
import { useFpCampaignProductsService } from '@cart-services/hooks/useFpCampaignProductsService'
import { type CountryCode, type ProjectCode } from '@constants'
import { sendFastpassPurchaseEvent } from '@dni-analysis'
import { generateItemVariant, normalizeAttributes } from '@dni-analysis/events/utils'
import { useEffectOnce } from '@dni-react-hooks/use-effect-once'
import { usePrevious } from '@dni-react-hooks/use-previous'
import { useApiCampaignActivityInfo } from '@hooks-api/fortune/useApiCampaignActivityInfo'
import { useApiCampaignMember } from '@hooks-api/fortune/useApiCampaignMember'
import { useApiGetGuestPaymentTransactionRequest } from '@hooks-api/fortune/useApiGetGuestPaymentTransactionRequest'
import { useApiCampaignPaymentTransactionBelongingToMember } from '@hooks-api/fortune-fastpass/use-api-campaign-payment-transaction-belonging-to-member'
import { MemberLogins } from '@hooks-api/types/dataModel/fortuneCampaign'
import { FastpassOrderStatus } from '@hooks-api/types/dataModel/fpOrder'
import { PaymentMethodType } from '@hooks-api/types/dataModel/payment'
import { Box } from '@pets-ui/Box/Box'
import { type SnackbarVariantsProps } from '@pets-ui/Snackbar/SnackbarVariants'
import { useSnackbar } from '@pets-ui/Snackbar/useSnackbar'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { queryStringify } from '@utils'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom-v5'
import CheckoutProgress from '../../components/checkout-progress'
import { DialogGuidedLogin } from '../../components/guided-login/DialogGuidedLogin'
import { DialogOrderAttributionFail } from '../../components/guided-login/DialogOrderAttributionFail'
import { OrderCancelled, OrderComplete } from '../../components/icon'
import Loading from '../../components/loading'
import { OrderInfo } from '../../components/order-info'
import { OrderStatus } from '../../components/order-status'
import QRcodeOrderStatus from '../../components/qrcode-order-status'
import { ThankyouFooter } from '../../components/thankyou-footer'
import { TheCampaignHeader } from '../../components/TheCampaignHeader'
import { GUIDED_LOGIN_KEY, storageGuidedLogin } from '../../hooks/apis/storage-guided-login'
import { useCampaign } from '../../hooks/use-campaign'
import { useFirebaseOrder } from '../../hooks/use-firebase-order'
import { OrderModel } from '../../model/order-model'
import style from '../../style/pages/client-thankyou.module.scss'
import { hasTransactionGiveaway } from '../../utils/transaction-giveaway'

const OFFLINE_STEP = 3
const ONLINE_STEP = 2

const ClientThankyou = () => {
  const params = useParams<{ orderHash: string }>()
  const orderHash = params?.orderHash
  const history = useHistory()
  const { t } = useTranslation()
  const { destructuredData: orderDetail, isLoading, mutate } = useApiGetGuestPaymentTransactionRequest({ orderHash })
  const { trigger: triggerPaymentTransactionBelongingToMember, isMutating: isMutatingPaymentTransactionBelongingToMember } = useApiCampaignPaymentTransactionBelongingToMember()
  const { campaignCodeFromUrl } = useCampaign()
  const ckoPaymentId = new URLSearchParams(window.location.search).get('cko-payment-id')
  const { isCkoExecuting } = useFpCkoRedirectApmExecution({
    ckoPaymentId,
    paymentTransactionId: orderDetail?.id,
    paymentSuccessUrl: `${window.location.origin}/campaigns/${campaignCodeFromUrl}/thankyou/${orderHash}`,
    paymentFallbackUrl: `${window.location.origin}/campaigns/${campaignCodeFromUrl}/payments?${queryStringify({ orderHash, paymentError: true })}`,
    onReconciled: mutate,
  })
  const { destructuredData: exhibitorSettings } = useApiCampaignActivityInfo(campaignCodeFromUrl)
  const campaignId = exhibitorSettings?.campaign_id ?? undefined
  const { region: countryCode } = exhibitorSettings || {}
  const { projectCode } = useFpCampaignProductsService(campaignCodeFromUrl)
  const { destructuredData: campaignMember } = useApiCampaignMember({ code: campaignCodeFromUrl })
  const { openSnackbar } = useSnackbar<SnackbarVariantsProps>()
  const { resetCartValues } = useFpCampaignCartService(exhibitorSettings?.campaign_id ?? null)
  const { updatedAt } = useFirebaseOrder({
    code: campaignCodeFromUrl,
    orderHash,
  })

  const { isExpired: isGuidedLoginExpired, setExpiredTimestamp } = storageGuidedLogin({
    key: GUIDED_LOGIN_KEY.THANK_YOU_PAGE,
    campaignCode: campaignCodeFromUrl,
  })

  const [isGuidedLoginDialogExpired, setIsGuidedLoginDialogExpired] = useState(isGuidedLoginExpired)

  const handleCloseDialog = () => {
    setExpiredTimestamp(exhibitorSettings?.end_time ? new Date(exhibitorSettings.end_time).getTime() : Infinity)
    setIsGuidedLoginDialogExpired(false)
  }
  const handleLogin = () => {
    setIsGuidedLoginDialogExpired(false)
    history.push(`/campaigns/${campaignCodeFromUrl}/login?orderHash=${orderHash}`)
  }

  const [isDialogGuidedLoginSuccessOpen, setIsDialogGuidedLoginSuccessOpen] = useState(false)

  const handlePaymentTransactionBelongingToMember = useCallback(async () => {
    try {
      await triggerPaymentTransactionBelongingToMember({
        code: campaignCodeFromUrl,
        order_hash: orderHash,
      })
      openSnackbar({
        message: '歸戶成功',
        severity: 'success',
      })
    }
    catch (error) {
      openSnackbar({
        message: '歸戶失敗',
        severity: 'error',
      })
      if (!isDialogGuidedLoginSuccessOpen) {
        setIsDialogGuidedLoginSuccessOpen(true)
      }
      console.error(error)
    }
  }, [campaignCodeFromUrl, isDialogGuidedLoginSuccessOpen, openSnackbar, orderHash, triggerPaymentTransactionBelongingToMember])

  useEffect(() => {
    const isGuidedLoginSuccess = new URLSearchParams(window.location.search).get('guidedLogin') === 'success'
    if (!isGuidedLoginSuccess) return
    window.history.replaceState(null, '', window.location.pathname)
    handlePaymentTransactionBelongingToMember()
  }, [handlePaymentTransactionBelongingToMember])

  useEffect(() => {
    if (orderDetail?.transaction_id) {
      sendFastpassPurchaseEvent('campaign', {
        campaign_code: campaignCodeFromUrl,
        transaction_id: orderDetail.id,
        currency: orderDetail.currency,
        value: orderDetail.total,
        items: orderDetail.child_orders.flatMap(childOrder => childOrder.order_items.map(item => ({
          currency: orderDetail.currency,
          item_id: String(item.variation_id ?? ''),
          item_name: item.product_name,
          item_variant: generateItemVariant({
            productName: item.product_name,
            attributes: normalizeAttributes(item.attributes),
          }),
          price: item.sale_price ?? 0,
          quantity: item.quantity,
          item_brand: childOrder.project_code,
        }))),
      })
    }
  }, [orderDetail, campaignCodeFromUrl])

  const previousUpdatedAt = usePrevious(updatedAt)
  useEffect(() => {
    if (!previousUpdatedAt || !updatedAt) return
    if (previousUpdatedAt !== updatedAt) {
      mutate()
    }
  }, [previousUpdatedAt, updatedAt, mutate])

  useEffect(() => {
    window.sessionStorage.removeItem('transactionId')
  }, [])

  useEffect(() => {
    if (!orderHash) history.push(`/campaigns/${campaignCodeFromUrl}`)
  }, [campaignCodeFromUrl, history, orderHash])

  useEffectOnce(() => {
    resetCartValues()
  })

  const orderModel = orderDetail && new OrderModel(orderDetail)
  const order = orderModel?.order
  const shouldShowGuiledLoginDialogExpired = !campaignMember && isGuidedLoginDialogExpired && exhibitorSettings?.member_login_type === MemberLogins.SUGGEST && !order?.customer_id
  const shouldShowQrCodeWithOrderStatus = order?.status !== FastpassOrderStatus.COMPLETED && order?.status !== FastpassOrderStatus.CANCELED && order?.status !== FastpassOrderStatus.REFUNDED
  const shouldShowQrCodeWithOrderGiveaway = !order?.item_confirmed && !!orderDetail?.has_offline_giveaway && !order?.is_giveaway_dispatch
  const shouldShowQrCode = shouldShowQrCodeWithOrderStatus || shouldShowQrCodeWithOrderGiveaway
  const currentStep = (order?.status !== FastpassOrderStatus.COMPLETED ? OFFLINE_STEP : ONLINE_STEP)
  const shouldRedirectToPaymentPage = order?.status === FastpassOrderStatus.PENDING && order.payment_method.type !== PaymentMethodType.OFFLINE_PAYMENT
  const hasGiveaways = hasTransactionGiveaway(orderDetail)

  useEffect(() => {
    if (shouldRedirectToPaymentPage) {
      history.push(`/campaigns/${campaignCodeFromUrl}/payments?orderHash=${orderHash}&redirectFromThankyou=true`)
    }
  }, [shouldRedirectToPaymentPage, campaignCodeFromUrl, history, orderHash])

  const OrderStatusIcon = () => {
    if (order?.status === FastpassOrderStatus.CANCELED || order?.status === FastpassOrderStatus.REFUNDED) {
      return (
        <div className={style.status}>
          <OrderCancelled />
          <span>{t('order.hasBeenCancelled')}</span>
        </div>
      )
    }
    return (
      <div className={style.status}>
        <OrderComplete />
        <span>
          {' '}
          {t('global.orderCompleted')}
        </span>
      </div>
    )
  }

  if (isLoading || isCkoExecuting) return <Loading />

  return (
    <>
      {shouldShowQrCode
      && (
        <Box backgroundColor={colors.white} pb={3}>
          <CheckoutProgress currentStep={currentStep} shouldShowCounterProps />
        </Box>
      )}
      <TheCampaignHeader projectCode={projectCode as ProjectCode} campaignId={campaignId as number} countryCode={countryCode as CountryCode} />
      <main className={style['thankyou-wrapper']}>
        {orderModel
        && (
          <>
            {shouldShowQrCode
              ? (
                <QRcodeOrderStatus
                  payment={orderDetail.payment_method?.type}
                  status={orderDetail.status}
                  isOfflinePayment={orderDetail.payment_method?.type.includes('offline')}
                  amount={orderDetail.total}
                  campaignID={campaignCodeFromUrl}
                  url={orderDetail.order_hash}
                  orderID={orderDetail.id}
                  isDispatch={orderDetail.is_giveaway_dispatch}
                  hasGiveaways={hasGiveaways}
                />
              )
              : (
                <>
                  <OrderStatusIcon />
                  <OrderStatus
                    payment={orderDetail.payment_method?.type}
                    status={orderDetail.status}
                    hasGiveaways={hasGiveaways}
                  />
                </>
              )}
            <OrderInfo order={orderModel} />
          </>
        )}
        <ThankyouFooter />
      </main>
      <DialogGuidedLogin open={shouldShowGuiledLoginDialogExpired} onCancel={handleCloseDialog} onConfirm={handleLogin} />
      <DialogOrderAttributionFail
        open={isDialogGuidedLoginSuccessOpen}
        onClose={() => setIsDialogGuidedLoginSuccessOpen(false)}
        onConfirm={handlePaymentTransactionBelongingToMember}
        isConfirmLoading={isMutatingPaymentTransactionBelongingToMember}
      />
    </>
  )
}
export default ClientThankyou
